import * as types from './mutation-types'
import axios from 'axios'
import { getTaskAPIData, getTaskGroupAPIData } from '../../services/Tasks'
import { apiErrorHandler } from '@/core/helpers/api.helpers'
import { allSettled } from '@/core/helpers/promise.helpers'

/**
 *  Boot tasks
 *
 * @param dispatch
 * @param rootGetters
 *
 * @returns {Promise<any>}
 */
export const boot = ({ dispatch, rootGetters }) => {
  const boot = rootGetters['auth/entitlements'].getAnyPassed({
    'fetchTaskTypes': { task: 'read', task_type: 'read' },
    'fetchTaskGroups': { task: 'read', task_type: 'read' },
    'fetchTaskOperations': { task: 'read', task_type: 'read' }
  }).map(action => dispatch(action)).map(allSettled)

  return Promise.all(boot)
}

/**
 * Fetch all task types
 *
 * @param commit
 */
export const fetchTaskTypes = ({ commit, rootGetters }) => {
  return new Promise((resolve, reject) => {
    rootGetters['auth/entitlements'].can({ task: 'read', task_type: 'read' }, { throwError: true })

    axios
      .get('/api/v1/task/types?limit=-1')
      .then((resp) => {
        commit(types.FETCH_TASK_TYPES, resp.data.items)
        resolve(resp)
      })
      .catch((error) => {
        const message = apiErrorHandler(error)
        reject(message)
      })
  })
}

/**
 * Fetch all task groups
 *
 * @param commit
 */
export const fetchTaskGroups = ({ commit, rootGetters }) => {
  return new Promise((resolve, reject) => {
    rootGetters['auth/entitlements'].can({ task: 'read', task_type: 'read' }, { throwError: true })

    axios
      .get('/api/v1/task/groups?limit=-1')
      .then((resp) => {
        commit(types.FETCH_TASK_GROUPS, resp.data.items)
        resolve(resp)
      })
      .catch((error) => {
        const message = apiErrorHandler(error)
        reject(message)
      })
  })
}

/**
 * Fetch task operations
 *
 * @param commit
 */
export const fetchTaskOperations = ({ commit, rootGetters }) => {
  return new Promise((resolve, reject) => {
    rootGetters['auth/entitlements'].can({ task: 'read' }, { throwError: true })

    axios
      .get('/api/v1/task/operations')
      .then((resp) => {
        commit(types.FETCH_TASK_OPERATIONS, resp.data.operations)
        resolve(resp)
      })
      .catch((error) => {
        const message = apiErrorHandler(error)
        reject(message)
      })
  })
}

/**
 * Promote task group
 *
 * @param taskGroupId
 * @param promoted
 */
export const promoteTaskGroup = ({ state, getters, dispatch }, { taskGroupId, promote = true }) => {
  return Promise.resolve()
    .then(() => {
      if ((state.taskGroups[taskGroupId].position !== -1 && promote) || (state.taskGroups[taskGroupId].position === -1 && !promote)) {
        return { position: state.taskGroups[taskGroupId].position, message: '' }
      }

      return axios.put(`/api/v1/task/groups/${taskGroupId}/promote`, { promote: promote })
        .then((resp) => {
          return dispatch('fetchTaskGroups')
            .then(() => {
              return { message: `${state.taskGroups[taskGroupId].name} ${promote ? ' Set to Default' : ' Removed from Default'} task group`, position: resp.data.position }
            })
        })
    })
    .catch(error => {
      throw Error(error.response && 'status_message' in error.response.data ? error.response.data.status_message : 'Network Error')
    })
}

/**
 * Create a new task group
 *
 * @param commit
 * @param state
 * @param formData
 * @returns {Promise<any>}
 */
export const createTaskGroup = ({ commit, state, rootGetters }, formData) => {
  return new Promise((resolve, reject) => {
    rootGetters['auth/entitlements'].can({ task: 'write' }, { throwError: true })

    const createTaskGroupHelper = (tasksIds) => {
      const payload = getTaskGroupAPIData(formData, tasksIds)
      axios.post('/api/v1/task/groups', payload)
        .then(resp => {
          commit(types.CREATE_TASK_GROUP, resp.data)
          resolve(resp.data.id)
        })
        .catch((resp) => {
          reject(resp.data)
        })
    }

    if (formData.taskGroup || formData.taskGroup === 0) {
      const taskIds = state.taskGroups[formData.taskGroup].task_list.map(task => task.task.id)
      createTaskGroupHelper(taskIds)
    } else {
      axios
        .all(formData.tasks.map(task => {
          const payload = getTaskAPIData(task)
          return axios.post('/api/v1/task/tasks', payload)
        }))
        .then(responses => {
          const taskIds = responses.map(resp => resp.data.id)
          createTaskGroupHelper(taskIds)
        })
        .catch((resp) => {
          reject(resp.data)
        })
    }
  })
}

/**
 * Update the task type json_schema and select_previous
 *
 * @param commit
 * @param taskId
 * @param jsonSchema
 * @param selectPrevious
 * @returns {Promise<any>}
 */
export const changeTaskTypeSchema = ({ commit }, { taskId, name, shortCode, duration, jsonSchema, selectPrevious }) => {
  return new Promise((resolve, reject) => {
    const payload = {
      name,
      duration: parseInt(duration),
      short_code: shortCode,
      json_schema: jsonSchema,
      select_previous: selectPrevious
    }

    axios.put(`/api/v1/task/types/${taskId}`, payload)
      .then((response) => {
        fetchTaskTypes({ commit }).then(resolve(response))
      })
      .catch((error) => {
        const handledError = apiErrorHandler(error)
        reject(handledError)
      })
  })
}

/**
 * Update the task type json_schema and select_previous
 *
 * @param commit
 * @param taskId
 * @param jsonSchema
 * @param selectPrevious
 * @returns {Promise<any>}
 */
export const createTaskType = ({ commit }, { name, shortCode, jsonSchema, selectPrevious, duration }) => {
  return new Promise((resolve, reject) => {
    const payload = {
      name,
      short_code: shortCode,
      json_schema: jsonSchema,
      select_previous: selectPrevious,
      duration: parseInt(duration)
    }

    axios.post('/api/v1/task/types', payload)
      .then((response) => {
        fetchTaskTypes({ commit }).then(resolve(response))
      })
      .catch((error) => {
        const handledError = apiErrorHandler(error)
        reject(handledError)
      })
  })
}

/**
 * Reset stored data
 *
 * @param commit
 */
export const clear = ({ commit }) => {
  commit(types.CLEAR)
}

export default {
  boot,
  fetchTaskTypes,
  fetchTaskGroups,
  fetchTaskOperations,
  promoteTaskGroup,
  createTaskGroup,
  changeTaskTypeSchema,
  createTaskType,
  clear
}
